import WarningIcon from "@redotech/redo-icons/alert-circle.svg";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import { JSXElementConstructor, memo } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { BgColorValue, RadiusValue } from "../../theme/box";
import { RedoButton, RedoButtonHierarchy } from "../buttons/redo-button";
import {
  FeaturedIconColor,
  RedoFeaturedIcon,
} from "../featured-icon/redo-featured-icon";
import * as css from "./redo-alert.module.css";

export const alertColor = [
  "default",
  "brand",
  "gray",
  "error",
  "warning",
  "success",
] as const;
export type AlertColor = (typeof alertColor)[number];

export const alertStyle = ["floating"] as const;
export type AlertStyle = (typeof alertStyle)[number];

export interface AlertProps {
  title: string;
  description?: string;
  Icon?: JSXElementConstructor<any> | null;
  color?: AlertColor;
  actions?: {
    text: string;
    action: "dismiss" | (() => void);
    hierarchy?: RedoButtonHierarchy;
  }[];
  showCloseButton?: boolean;
  onClose?: () => void;
  fullWidth?: boolean;
  dir?: "row" | "column";
  align?: "center" | "flex-start" | "flex-end" | "stretch" | "baseline";
  radius?: RadiusValue;
  bgColor?: BgColorValue;
  uiSelector?: string;
}

// TODO: IMPLEMENT FULL WIDTH AND ADD BUTTONS
export const RedoAlert = memo(function RedoAlert({
  title,
  description,
  Icon = WarningIcon,
  color = "default",
  actions,
  showCloseButton = true,
  onClose,
  fullWidth = false,
  dir = "row",
  align,
  radius = "xl",
  bgColor = "primary-alt",
  uiSelector,
}: AlertProps) {
  return (
    <Flex
      bgColor={bgColor}
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      data-ui={uiSelector}
      gap="xl"
      p="xl"
      position="relative"
      radius={radius}
      w={fullWidth ? "full" : undefined}
    >
      {showCloseButton ? (
        <Flex className={css.closeButton}>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={CloseIcon}
            onClick={onClose}
          />
        </Flex>
      ) : null}
      <Flex
        align={align ?? (dir === "row" ? "center" : "flex-start")}
        dir={dir}
        gap="xl"
      >
        {Icon ? (
          <Flex alignSelf={dir === "column" ? "flex-start" : undefined}>
            <RedoFeaturedIcon
              color={alertColorToFeaturedIcon[color]}
              Icon={Icon}
              size="sm"
            />
          </Flex>
        ) : null}
        <Flex align="flex-start" dir="column" gap="md" grow={1}>
          <Text fontSize="sm" fontWeight="semibold" textColor="secondary">
            {title}
          </Text>
          {description ? (
            <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
              {description}
            </Text>
          ) : null}
          {actions ? (
            <Flex>
              {actions.map((action) => (
                <RedoButton
                  hierarchy={action.hierarchy ?? "tertiary"}
                  key={action.text}
                  onClick={
                    action.action === "dismiss" ? onClose : action.action
                  }
                  text={action.text}
                />
              ))}
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  );
});

const alertColorToFeaturedIcon: Record<AlertColor, FeaturedIconColor> = {
  default: "black",
  brand: "brand",
  gray: "gray",
  error: "error",
  warning: "warning",
  success: "success",
};
